/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
  ReactNode,
  ReactElement,
  MouseEvent as ReactMouseEvent,
  ElementType,
  forwardRef,
} from 'react';
import { usePublishEvent } from '@agilelab/plugin-wb-events';
import { externalLinkClickedTopic } from '@agilelab/plugin-wb-events-common';
// eslint-disable-next-line no-restricted-imports
import MaterialLink, {
  LinkProps as MaterialLinkProps,
} from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import {
  // eslint-disable-next-line no-restricted-imports
  Link as RouterLink,
  LinkProps as RouterLinkProps,
} from 'react-router-dom';
import { createRoutesFromChildren, Route, useLocation } from 'react-router';
import { WbTooltip } from '../WbTooltip';
import { configApiRef, useApi } from '@mainstage/core-plugin-api';
import { trimEnd } from 'lodash';
import clsx from 'clsx';

// ─── XSS protection ──────────────────────────────────────────────────────────

// See https://github.com/facebook/react/blob/f0cf832e1d0c8544c36aa8b310960885a11a847c/packages/react-dom-bindings/src/shared/sanitizeURL.js
const scriptProtocolPattern =
  // eslint-disable-next-line no-control-regex
  /^[\u0000-\u001F ]*j[\r\n\t]*a[\r\n\t]*v[\r\n\t]*a[\r\n\t]*s[\r\n\t]*c[\r\n\t]*r[\r\n\t]*i[\r\n\t]*p[\r\n\t]*t[\r\n\t]*\:/i;

const originalWindowOpen = window.open as typeof window.open & {
  __wb?: true;
};
if (originalWindowOpen && !originalWindowOpen.__wb) {
  const newOpen = function open(
    this: Window,
    ...args: Parameters<typeof window.open>
  ) {
    if (scriptProtocolPattern.test(String(args[0]))) {
      throw new Error(
        'Rejected window.open() with a javascript: URL as a security precaution',
      );
    }
    return originalWindowOpen.apply(this, args);
  };
  newOpen.__wb = true;
  window.open = newOpen;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

export function isReactRouterBeta(): boolean {
  const [obj] = createRoutesFromChildren(<Route index element={<div />} />);
  return !obj.index;
}

const useBaseUrl = () => {
  try {
    const config = useApi(configApiRef);
    return config.getOptionalString('app.baseUrl');
  } catch {
    return undefined;
  }
};

const useBasePath = () => {
  const base = 'http://sample.dev';
  const url = useBaseUrl() ?? '/';
  const { pathname } = new URL(url, base);
  return trimEnd(pathname, '/');
};

export const isExternalUri = (uri: string) => /^([a-z+.-]+):/.test(uri);

export const useResolvedPath = (uri: string) => {
  let resolvedPath = String(uri);
  const basePath = useBasePath();
  const external = isExternalUri(resolvedPath);
  const startsWithBasePath = resolvedPath.startsWith(basePath);
  if (!external && !startsWithBasePath) {
    resolvedPath = basePath.concat(resolvedPath);
  }
  return resolvedPath;
};

const isExternalHttpLink = (uri: string): boolean => {
  if (!uri.match(/^https?:\/\//)) return false;
  const linkHost = uri.replace(/^https?:\/\//, '').split('/')[0];
  const currentHost = document.URL.replace(/^https?:\/\//, '').split('/')[0];
  return linkHost !== currentHost;
};

const getNodeText = (node: ReactNode): string => {
  if (node instanceof Array) return node.map(getNodeText).join(' ').trim();
  if (typeof node === 'object' && node)
    return getNodeText((node as ReactElement)?.props?.children);
  if (['string', 'number'].includes(typeof node)) return String(node);
  return '';
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles(
  () => ({
    visuallyHidden: {
      clip: 'rect(0 0 0 0)',
      clipPath: 'inset(50%)',
      overflow: 'hidden',
      position: 'absolute',
      userSelect: 'none',
      whiteSpace: 'nowrap',
      height: 1,
      width: 1,
    },
    truncate: {
      textOverflow: 'ellipsis',
      overflow: 'hidden',
    },
  }),
  { name: 'WbLink' },
);

// ─── Props ────────────────────────────────────────────────────────────────────

export type WbLinkBaseProps = Omit<MaterialLinkProps, 'to'> &
  Omit<RouterLinkProps, 'to'> & {
    to: string;
    component?: ElementType<any>;
    /**
     * Optionally override the link target.
     * For external links Backstage defaults to `_blank` with `rel="noopener"`.
     * @example _blank
     */
    hrefTarget?: string;
  };

export type WbLinkProps = WbLinkBaseProps & {
  /** Show a WbTooltip wrapping the link. */
  showTooltip?: boolean;
  /** Truncate the text */
  truncate?: boolean;
};

// ─── WbUnstyledLink ───────────────────────────────────────────────────────────

/**
 * Ref-forwarding unstyled primitive:
 * - RouterLink for internal URIs
 * - <a> for external URIs (with target/_blank, rel, screen-reader text)
 * - XSS block on javascript: URLs
 * - Publishes externalLinkClickedTopic for external http(s) links
 * - block-row-click workaround for table rows
 *
 * Ideal as the `component` prop of MUI components (e.g. `IconButton`, `Button`)
 * when you need routing + accessibility without link styling.
 *
 * @example
 * // Pass as component to IconButton for an accessible icon link
 * <IconButton component={WbUnstyledLink} to="/catalog" aria-label="Go to catalog">
 *   <CatalogIcon />
 * </IconButton>
 */
export const WbUnstyledLink = forwardRef<any, WbLinkBaseProps>(
  ({ onClick, hrefTarget, style, ...props }, ref) => {
    const classes = useStyles();
    const { publish } = usePublishEvent({ generatorSystem: 'platform' });
    const location = useLocation();

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const to = isReactRouterBeta() ? useResolvedPath(props.to) : props.to;

    if (scriptProtocolPattern.test(to)) {
      throw new Error(
        'WbLink rejected javascript: URL as a security precaution',
      );
    }

    const external = isExternalUri(to);
    const newWindow = external && !!/^https?:/.exec(to);

    const handleClick = async (event: ReactMouseEvent<any, MouseEvent>) => {
      onClick?.(event);
      if (isExternalHttpLink(to)) {
        publish(externalLinkClickedTopic, {
          externalUrl: to,
          currentPage: location.pathname,
        });
      }
    };

    const mergedStyle = { cursor: 'pointer', ...style };

    const extraProps =
      // this is a workaround to avoid the click propagating on the table row when this is used in tables and
      // the row already has an onClick handler
      { 'block-row-click': 'true' };

    return external ? (
      <a
        {...(newWindow
          ? {
              target: hrefTarget ?? '_blank',
              rel: 'noopener noreferrer',
            }
          : {})}
        {...props}
        {...(props['aria-label']
          ? { 'aria-label': `${props['aria-label']}, Opens in a new window` }
          : {})}
        ref={ref}
        href={to}
        style={mergedStyle}
        onClick={handleClick}
        className={props.className}
        {...extraProps}
      >
        {props.children}
        <Typography component="span" className={classes.visuallyHidden}>
          , Opens in a new window
        </Typography>
      </a>
    ) : (
      <RouterLink
        {...props}
        ref={ref}
        to={to}
        style={mergedStyle}
        onClick={handleClick}
        {...extraProps}
      />
    );
  },
);

// ─── WbLink ─────────────────────────────────────────────────────────────────

/**
 * Full-featured link component:
 * - MUI Link styling (underline, color, variant...)
 * - RouterLink for internal URIs, <a> for external
 * - XSS protection on javascript: URLs
 * - Publishes externalLinkClickedTopic for external http(s) links
 * - Optional WbTooltip
 * - ref forwarding — safe to use as `component` prop of MUI components
 *
 * @example
 * // Simple internal link
 * <WbLink to="/catalog">Go to catalog</WbLink>
 *
 * @example
 * // External link with tooltip
 * <WbLink to="https://example.com" showTooltip>
 *   External docs
 * </WbLink>
 */
export const WbLink = forwardRef<any, WbLinkProps>(
  ({ showTooltip, truncate, className, ...props }, ref) => {
    const classes = useStyles();
    const tooltipTitle =
      typeof props.children === 'string'
        ? props.children
        : getNodeText(props.children) || props.to;

    const link = (
      <MaterialLink
        underline="always"
        {...props}
        className={clsx(truncate && classes.truncate, className)}
        ref={ref}
        component={WbUnstyledLink}
      />
    );

    return showTooltip ? (
      <WbTooltip title={tooltipTitle}>{link}</WbTooltip>
    ) : (
      link
    );
  },
) as (props: WbLinkProps & { ref?: any }) => JSX.Element;
